// Remote cache for active loon-pack products. The /products endpoint is
// identical for every caller, so one shared cache entry is sufficient.

import { useMemo } from 'react';
import type { ProductDocument } from '@airhead-games/shared/types';
import { getActiveLoonProducts } from '@airhead-games/shared/utilities';
import { getAllActiveProducts } from '../api/publicApi';
import { createRemoteCache } from './createRemoteCache';

export const productCache = createRemoteCache<ProductDocument[]>({
  name: 'products',
  initialData: [],
  fallbackError: 'Failed to load products. Please try again.',
  fetcher: async () => {
    const response = await getAllActiveProducts();
    return getActiveLoonProducts(response.data.products);
  },
});

interface UseProductsOptions {
  autoFetch?: boolean;
}

export interface UseProductsResult {
  products: ProductDocument[];
  loading: boolean;
  error: string | null;
  isFetched: boolean;
  refetch: () => Promise<void>;
}

export function useLoonProducts(
  options: UseProductsOptions = {}
): UseProductsResult {
  const cache = productCache.useCache({ autoFetch: options.autoFetch });
  return useMemo(
    () => ({
      products: cache.data,
      loading: cache.loading,
      error: cache.error,
      isFetched: cache.isFetched,
      refetch: cache.refetch,
    }),
    [cache]
  );
}
